/**
 * The progression runtime: the profile, the claim ledger, the clock and the
 * storage behind the shop, pass and challenge screens.
 *
 * Same shape as `NetRuntime` and `TrainingRuntime`, and for the same reason: a
 * `MenuModel` that owned any of these could not be constructed in Node, and the
 * exhaustive navigation search in test/ui would be reading localStorage. The
 * model gets a `ProgressionState` snapshot (see ./progression-model) and knows
 * nothing about where it came from.
 *
 * THE CLOCK IS INJECTED. `now()` defaults to `Date.now`, and that default is
 * the ONLY wall-clock read in the whole feature — everything in src/game takes
 * an epoch-ms argument. A test hands in its own and can therefore be on a
 * Wednesday, on a year boundary, or in a season that has not happened yet.
 *
 * CREDITS ARE EARNED. Nothing here has a purchase path and nothing may grow
 * one; see the header of src/game/cosmetics.
 */

import { Rng } from '../physics/rng';
import {
  applyMatchStats,
  challengeById,
  claimChallenge,
  claimableChallenges,
  refreshChallenges,
  unlockedSeasonStage,
  weekEnd,
} from '../game/challenges';
import {
  claimAll,
  claimTier,
  emptyPassClaims,
  loadPassClaims,
  savePassClaims,
  unlockPremium,
  type PassClaims,
  type PassTrack,
} from '../game/rocketpass';
import { buyShopEntry, rollShop, shopDay, shopRefreshAt } from '../game/shop';
import {
  awardMatch,
  defaultProfile,
  loadProfile,
  saveProfile,
  type ProfileStorage,
} from '../game/profile';
import { itemById } from '../game/items';
import type { ProfileOp } from '../game/account';
import type { MatchStats, PlayerProfile, ShopEntry } from '../game/cosmetics';
import type { ProgressionState, ProgressionSummary } from './progression-model';

export interface ProgressionHooks {
  /** The state moved; re-render. */
  changed(): void;
  /** A one-line message for the note strip. */
  toast(text: string): void;
}

export interface ProgressionOptions {
  /**
   * Test-only storage. In the shipped game this DEFAULTS TO NULL: since
   * accounts arrived the profile lives in the server's database, arrives via
   * `adoptServer`, and is never written to localStorage. An anonymous player
   * gets the starter profile in memory and the account-gated screens are
   * locked anyway.
   */
  storage?: ProfileStorage | null;
  /**
   * Epoch milliseconds. THE only clock read in this feature — see the header.
   * A test passes a function it controls.
   */
  now?: () => number;
  /**
   * Seed for the award and claim generators. A constant rather than a clock
   * reading, so a match played from the same state awards the same drops and a
   * bug in one is reproducible from a description. It advances with use and is
   * not persisted, which is deliberate: the drops a player has already been
   * given are in their inventory, and re-deriving them is not something
   * anything needs to do.
   */
  seed?: number;
}

/** What a claim or a purchase did, for the toast. */
export interface ProgressionResult {
  ok: boolean;
  message: string;
}

const PROGRESSION_SEED = 0x9d2c_5680;

export class ProgressionRuntime {
  private profile: PlayerProfile;
  private claims: PassClaims;
  private readonly storage: ProfileStorage | null;
  private readonly clock: () => number;
  private readonly rng: Rng;

  /**
   * Where mutations are relayed when a player is signed in. Every commit that
   * changes the profile also tells the server, and the server's answer comes
   * back through `adoptServer` — so what this class computes locally is an
   * immediate prediction, and the database stays the authority.
   */
  private remote: { push(op: ProfileOp): void } | null = null;

  /** The last match's award, for the full-time screen. Null before one lands. */
  private summary: ProgressionSummary | null = null;

  constructor(
    private readonly hooks: ProgressionHooks,
    options: ProgressionOptions = {}
  ) {
    this.storage = options.storage ?? null;
    this.clock = options.now ?? (() => Date.now());
    this.rng = new Rng(options.seed ?? PROGRESSION_SEED);

    this.profile = this.storage === null ? defaultProfile() : loadProfile(this.storage);
    this.claims = this.storage === null ? emptyPassClaims() : loadPassClaims(this.storage);
    this.profile = refreshChallenges(this.profile, this.clock());
  }

  /** Wire (or unwire) the server relay. Set by the account runtime's owner. */
  setRemote(remote: { push(op: ProfileOp): void } | null): void {
    this.remote = remote;
  }

  /**
   * Adopt the server's profile and claims — at login, and after every answered
   * mutation. This is the only door server state comes in through, and it
   * REPLACES the local state wholesale: whatever the client predicted, the
   * database's answer wins.
   */
  adoptServer(profile: PlayerProfile, claims: PassClaims): void {
    this.profile = refreshChallenges(profile, this.clock());
    this.claims = claims;
    this.hooks.changed();
  }

  /** Back to the anonymous starter profile — the player signed out. */
  resetLocal(): void {
    this.profile = refreshChallenges(defaultProfile(), this.clock());
    this.claims = emptyPassClaims();
    this.summary = null;
    this.hooks.changed();
  }

  /**
   * A snapshot for the menu model.
   *
   * Rebuilt on demand rather than cached: it is read once per render and the
   * expensive part — the shop rotation — is a pure function of the day, so a
   * cache would only ever be an opportunity to show yesterday's shop.
   */
  state(): ProgressionState {
    const now = this.clock();
    const day = shopDay(now);
    return {
      profile: this.profile,
      claims: this.claims,
      now,
      shop: rollShop(day),
      shopRefreshAt: shopRefreshAt(day),
      weekEndsAt: weekEnd(now),
      seasonStage: unlockedSeasonStage(this.profile),
    };
  }

  /** The last match's award, or null. */
  get lastSummary(): ProgressionSummary | null {
    return this.summary;
  }

  get credits(): number {
    return this.profile.wallet.credits;
  }

  /**
   * Re-read the calendar: expire what has run out, add what has arrived.
   *
   * Called on every visit to a progression screen, because a tab left open
   * across a Wednesday would otherwise still be showing last week's three.
   */
  refresh(): void {
    const next = refreshChallenges(this.profile, this.clock());
    if (next === this.profile) return;
    this.commit(next);
  }

  // -------------------------------------------------------------------------
  // Shop
  // -------------------------------------------------------------------------

  buy(entry: ShopEntry): ProgressionResult {
    const result = buyShopEntry(this.profile, entry);
    if (!result.ok) {
      return {
        ok: false,
        message:
          result.reason === 'owned'
            ? 'You already own that.'
            : result.reason === 'unknown-item'
              ? 'That item is no longer in the catalogue.'
              : `Not enough Credits — that costs ${entry.price}, and you have ${this.credits}.`,
      };
    }
    this.commit(result.profile);
    this.remote?.push({
      kind: 'buy',
      itemId: entry.itemId,
      ...(entry.paint === undefined ? {} : { paint: entry.paint }),
    });
    const name = itemById(entry.itemId)?.name ?? entry.itemId;
    return { ok: true, message: `${name} bought for ${result.spent} Credits.` };
  }

  // -------------------------------------------------------------------------
  // Rocket Pass
  // -------------------------------------------------------------------------

  claimTier(tier: number, track: PassTrack): ProgressionResult {
    const result = claimTier(this.profile, this.claims, tier, track);
    if (!result.ok) {
      return {
        ok: false,
        message:
          result.reason === 'locked'
            ? `Tier ${tier} is not unlocked yet.`
            : result.reason === 'not-premium'
              ? 'That reward is on the premium track.'
              : result.reason === 'already-claimed'
                ? 'Already claimed.'
                : 'Nothing to claim on that tier.',
      };
    }
    this.claims = result.claims;
    this.commit(result.profile);
    this.remote?.push({ kind: 'claim-tier', tier, track });
    return { ok: true, message: `Tier ${tier} claimed.` };
  }

  claimAllTiers(): ProgressionResult {
    const result = claimAll(this.profile, this.claims);
    if (result.claimed === 0) return { ok: false, message: 'Nothing to claim yet.' };
    this.claims = result.claims;
    this.commit(result.profile);
    this.remote?.push({ kind: 'claim-all-tiers' });
    return {
      ok: true,
      message: `${result.claimed} tier${result.claimed === 1 ? '' : 's'} claimed.`,
    };
  }

  unlockPremium(): ProgressionResult {
    const result = unlockPremium(this.profile);
    if (!result.ok) {
      return {
        ok: false,
        message:
          result.reason === 'already-premium'
            ? 'The premium track is already yours.'
            : 'Not enough Credits yet. They are earned by playing.',
      };
    }
    // The premium weeklies exist from this moment, so the calendar is re-read
    // before the profile is written rather than on the next screen visit.
    this.commit(refreshChallenges(result.profile, this.clock()));
    this.remote?.push({ kind: 'unlock-premium' });
    return { ok: true, message: `Premium track unlocked for ${result.spent} Credits.` };
  }

  // -------------------------------------------------------------------------
  // Challenges
  // -------------------------------------------------------------------------

  claimChallenge(id: string): ProgressionResult {
    const result = claimChallenge(this.profile, id, this.clock(), this.rng);
    if (!result.ok) {
      return {
        ok: false,
        message:
          result.reason === 'incomplete'
            ? 'Not finished yet.'
            : result.reason === 'expired'
              ? 'That challenge has expired.'
              : result.reason === 'already-claimed'
                ? 'Already claimed.'
                : 'That challenge is no longer available.',
      };
    }
    this.commit(result.profile);
    this.remote?.push({ kind: 'claim-challenge', id });
    const name = challengeById(id)?.name ?? id;
    const tiers = result.tiers > 0 ? `, ${result.tiers} tier${result.tiers === 1 ? '' : 's'}` : '';
    return { ok: true, message: `${name}: +${result.tierPoints} Tier Points${tiers}.` };
  }

  claimAllChallenges(): ProgressionResult {
    const now = this.clock();
    const ready = claimableChallenges(this.profile, now);
    if (ready.length === 0) return { ok: false, message: 'Nothing to claim yet.' };

    let profile = this.profile;
    let points = 0;
    for (const entry of ready) {
      const result = claimChallenge(profile, entry.id, now, this.rng);
      if (!result.ok) continue;
      profile = result.profile;
      points += result.tierPoints;
    }
    this.commit(profile);
    this.remote?.push({ kind: 'claim-all-challenges' });
    return { ok: true, message: `${ready.length} claimed — ${points} Tier Points.` };
  }

  // -------------------------------------------------------------------------
  // Match award
  // -------------------------------------------------------------------------

  /**
   * Everything a finished match does to the profile.
   *
   * `awardMatch` (src/game/profile.ts) owns the XP, the Credits, the level-up
   * drops and the Tier Points a level is worth; this adds the challenge
   * progress, which is the half that belongs to the calendar. Both are folded
   * into ONE summary, because "you gained a level and finished two challenges"
   * is one thing that happened to the player.
   *
   * The calendar is re-read FIRST: a match played across a Wednesday must count
   * towards the week it finished in, not the week it started in.
   */
  recordMatch(stats: MatchStats, won: boolean, mode?: string): ProgressionSummary {
    const now = this.clock();
    const before = refreshChallenges(this.profile, now);

    const award = awardMatch(before, stats, won, this.rng);
    const applied = applyMatchStats(award.profile, stats, now, mode);

    const summary: ProgressionSummary = {
      xpGained: award.xpGained,
      levelsGained: award.levelsGained,
      level: applied.profile.progress.level,
      creditsGained: award.creditsGained,
      tierPointsGained: award.tierPointsGained,
      tiersGained: award.tiersGained,
      passTier: applied.profile.progress.passTier,
      challengesCompleted: applied.completed.map(e => challengeById(e.id)?.name ?? e.id),
      drops: award.drops.map(d => itemById(d.itemId)?.name ?? d.itemId),
    };

    this.summary = summary;
    this.commit(applied.profile);
    // A local match is client-measured by nature; the server clamps the stats
    // to plausibility before paying. See `recordLocalMatch` in server/accounts.
    this.remote?.push({ kind: 'match', stats, won, ...(mode === undefined ? {} : { mode }) });
    return summary;
  }

  /** Throw the last summary away, so a fresh match does not show a stale one. */
  clearSummary(): void {
    this.summary = null;
  }

  // -------------------------------------------------------------------------

  /**
   * Adopt a new profile and write both stores.
   *
   * One place, so nothing can move the profile without persisting it and
   * without the view being told. A failed write is not an error: `saveProfile`
   * returns false when storage is full or disabled, and the session carries on
   * with what it has in memory.
   */
  private commit(profile: PlayerProfile): void {
    this.profile = profile;
    saveProfile(this.profile, this.storage);
    savePassClaims(this.claims, this.storage);
    this.hooks.changed();
  }

  /**
   * Adopt a profile edited elsewhere — the garage equipping an item, changing a
   * loadout colour or switching preset.
   *
   * The garage does not get its own store. One owner of the profile is what
   * makes an item bought on the shop screen equippable on the garage screen
   * without either of them knowing the other exists, and it is what stops two
   * runtimes writing the same localStorage key over each other's edits.
   */
  applyProfile(profile: PlayerProfile): void {
    this.commit(profile);
    // Only the garage's half is the client's to say: which items are ARRANGED
    // where. What is owned is the server's, and it re-checks ownership of every
    // equipped instance before adopting this.
    this.remote?.push({
      kind: 'loadout',
      presets: profile.presets,
      activePresetId: profile.activePresetId,
    });
  }

  /** For tests and for the garage: the live profile, by reference. */
  get current(): PlayerProfile {
    return this.profile;
  }

  /** A one-line message for the note strip, from a caller that has one. */
  toast(text: string): void {
    this.hooks.toast(text);
  }
}
